/**
 * ExcludedMachinesTab
 * Table of excluded machines with add/remove functionality
 */

import { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { Loader2, Plus, Trash2, Pencil, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/core/components/ui/sheet';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { Textarea } from '@/core/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';

const REASON_MAX_LENGTH = 500;

function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help shrink-0" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
import { useBillingStore } from '../../stores/billing-store';
import type { ExcludedMachine } from '../../types/billing-types';

export function ExcludedMachinesTab() {
  const {
    excludedMachines,
    isLoadingExcludedMachines,
    excludedMachinesError,
    loadExcludedMachines,
    addExcludedMachine,
    updateExcludedMachine,
    removeExcludedMachine,
  } = useBillingStore();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const pageSizeOptions = [10, 25, 50, 100];
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<ExcludedMachine | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ machineId: '', reason: '' });
  const [machineIdError, setMachineIdError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);

  const validateReason = (value: string): string | null => {
    if (value.length > REASON_MAX_LENGTH) return `Reason must be ${REASON_MAX_LENGTH} characters or fewer`;
    return null;
  };

  const openEdit = (machine: ExcludedMachine) => {
    setEditingMachine(machine);
    setFormData({ machineId: String(machine.machineId), reason: machine.reason ?? '' });
    setMachineIdError(null);
    setIsAddOpen(true);
  };

  const validateMachineId = (value: string): string | null => {
    if (!value.trim()) return 'Machine ID is required';
    const num = parseInt(value, 10);
    if (isNaN(num) || num <= 0) return 'Machine ID must be a positive number';
    if (num > 2_147_483_647) return 'Machine ID exceeds maximum integer value';
    if (excludedMachines.some((m) => m.machineId === num)) return 'This machine is already excluded';
    return null;
  };

  useEffect(() => {
    loadExcludedMachines();
  }, [loadExcludedMachines]);

  const handleSubmit = async () => {
    const rErr = validateReason(formData.reason);
    if (rErr) { setReasonError(rErr); return; }

    if (editingMachine) {
      setIsSubmitting(true);
      try {
        await updateExcludedMachine(editingMachine.machineId, formData.reason || null);
        toast.success(`Machine ${editingMachine.machineId} updated`);
        setIsAddOpen(false);
        setEditingMachine(null);
        setFormData({ machineId: '', reason: '' });
        setReasonError(null);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update excluded machine');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const error = validateMachineId(formData.machineId);
    if (error) { setMachineIdError(error); return; }

    const machineIdNum = parseInt(formData.machineId, 10);
    setIsSubmitting(true);
    try {
      await addExcludedMachine({ machineId: machineIdNum, reason: formData.reason || null });
      toast.success(`Machine ${machineIdNum} added to exclusion list`);
      setIsAddOpen(false);
      setFormData({ machineId: '', reason: '' });
      setMachineIdError(null);
      setReasonError(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add excluded machine');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (machineId: number) => {
    setDeletingId(machineId);
    try {
      await removeExcludedMachine(machineId);
      toast.success(`Machine ${machineId} removed from exclusion list`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove excluded machine');
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnDef<ExcludedMachine>[] = [
    {
      accessorKey: 'machineId',
      header: 'Machine ID',
      size: 120,
    },
    {
      accessorKey: 'reason',
      header: 'Reason',
      cell: ({ getValue }) => (getValue() as string | null) ?? '—',
    },
    {
      accessorKey: 'createdDt',
      header: 'Created',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? new Date(v).toLocaleDateString() : '—';
      },
      size: 110,
    },
    {
      accessorKey: 'createdByName',
      header: 'Created By',
      cell: ({ getValue }) => (getValue() as string | null) ?? '—',
      size: 140,
    },
    {
      accessorKey: 'updatedDt',
      header: 'Updated',
      cell: ({ getValue }) => {
        const v = getValue() as string | null;
        return v ? new Date(v).toLocaleDateString() : '—';
      },
      size: 110,
    },
    {
      accessorKey: 'updatedByName',
      header: 'Updated By',
      cell: ({ getValue }) => (getValue() as string | null) ?? '—',
      size: 140,
    },
    {
      id: 'actions',
      header: '',
      size: 90,
      cell: ({ row }) => {
        const machineId = row.original.machineId;
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEdit(row.original)}
              disabled={deletingId === machineId}
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDelete(machineId)}
              disabled={deletingId === machineId}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              {deletingId === machineId ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: excludedMachines,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  const totalCount = excludedMachines.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);
  const pagedRows = table.getRowModel().rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    startPage = endPage - maxPagesToShow + 1 < 1 ? 1 : endPage - maxPagesToShow + 1;
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end">
        <Button
          onClick={() => setIsAddOpen(true)}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
        >
          <Plus className="h-4 w-4" />
          Add Machine
        </Button>
      </div>

      {/* Error */}
      {excludedMachinesError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {excludedMachinesError}
        </div>
      )}

      {/* Table */}
      <div className={`rounded-md border ${GRID_STYLES.border} overflow-hidden`}>
        {isLoadingExcludedMachines ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : excludedMachines.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
            No excluded machines found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={cn(GRID_STYLES.header.background, 'sticky top-0 z-10')}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'cursor-pointer select-none whitespace-nowrap')}
                        style={{ width: header.getSize() }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <div className="flex flex-col">
                              <ChevronUp className={`h-3 w-3 ${header.column.getIsSorted() === 'asc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive}`} />
                              <ChevronDown className={`h-3 w-3 -mt-1 ${header.column.getIsSorted() === 'desc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive}`} />
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={row.id} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, `border-b ${GRID_STYLES.border}`, 'transition-colors')}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={cn(GRID_STYLES.cell.base, 'whitespace-nowrap')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex items-center justify-between px-2 py-2 border border-[#E8E8E8] rounded-b-md bg-white -mt-3 border-t-0">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map(pageNum => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentPage(pageNum)}
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626] border-[#FFCD00]' : 'border-[#E8E8E8]'}`}
              >
                {pageNum}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="h-8 w-8 p-0 border-[#E8E8E8]">
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage} of {totalPages} pages</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#606060]">Rows per page:</span>
              <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v, 10)); setCurrentPage(1); }}>
                <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map(size => (
                    <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Add Machine Sheet */}
      <Sheet
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) {
            setEditingMachine(null);
            setFormData({ machineId: '', reason: '' });
            setMachineIdError(null);
            setReasonError(null);
          }
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle>{editingMachine ? 'Edit Excluded Machine' : 'Add Excluded Machine'}</SheetTitle>
          </SheetHeader>

          <div className="space-y-5 mt-6">
            <div className="space-y-1.5">
              <Label htmlFor="machineId">
                Machine ID {!editingMachine && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="machineId"
                type="number"
                min={1}
                max={2_147_483_647}
                value={formData.machineId}
                onChange={(e) => {
                  if (editingMachine) return;
                  const val = e.target.value;
                  setFormData({ ...formData, machineId: val });
                  setMachineIdError(val ? validateMachineId(val) : null);
                }}
                placeholder="Enter machine ID"
                disabled={isSubmitting || !!editingMachine}
                className={machineIdError ? 'border-red-400 focus-visible:ring-red-400' : ''}
              />
              {machineIdError && (
                <p className="text-xs text-red-500">{machineIdError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="reason">Reason</Label>
                <InfoTooltip text="Optional explanation for why this machine is excluded from billing" />
              </div>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, reason: val });
                  setReasonError(val ? validateReason(val) : null);
                }}
                placeholder="Enter reason for exclusion (optional)"
                rows={4}
                disabled={isSubmitting}
                className={reasonError ? 'border-red-400 focus-visible:ring-red-400' : ''}
              />
              <div className="flex justify-between">
                {reasonError
                  ? <p className="text-xs text-red-500">{reasonError}</p>
                  : <span />
                }
                <p className={`text-xs ${formData.reason.length > REASON_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                  {formData.reason.length}/{REASON_MAX_LENGTH}
                </p>
              </div>
            </div>
          </div>

          <SheetFooter className="mt-8 pt-4 border-t flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddOpen(false);
                setEditingMachine(null);
                setFormData({ machineId: '', reason: '' });
                setMachineIdError(null);
                setReasonError(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {editingMachine ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                editingMachine ? 'Save Changes' : 'Add Machine'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
